import Link, { type LinkProps } from 'next/link';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'solid' | 'outline' | 'quiet';
type Size = 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-3 rounded-full border font-medium whitespace-nowrap transition-colors duration-250 disabled:cursor-not-allowed disabled:opacity-60';

const variants: Record<Variant, string> = {
  solid: 'border-ink bg-ink text-canvas hover:border-accent hover:bg-accent',
  outline: 'border-ink bg-transparent text-ink hover:bg-ink hover:text-canvas',
  quiet: 'border-line bg-surface/70 text-ink hover:border-ink',
};

const sizes: Record<Size, string> = {
  md: 'min-h-11 px-5 text-[15px]',
  lg: 'min-h-14 px-6.5 text-base',
};

type StyleProps = { variant?: Variant; size?: Size };

export function buttonClass({ variant = 'solid', size = 'lg' }: StyleProps = {}): string {
  return cn(base, variants[variant], sizes[size]);
}

export function Button({
  variant,
  size,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'> & StyleProps) {
  return <button className={cn(buttonClass({ variant, size }), className)} {...props} />;
}

export function ButtonLink({
  variant,
  size,
  className,
  children,
  ...props
}: LinkProps & StyleProps & { className?: string; children: ReactNode }) {
  return (
    <Link className={cn(buttonClass({ variant, size }), className)} {...props}>
      {children}
    </Link>
  );
}

export function ArrowRight() {
  return (
    <span aria-hidden className="text-lg leading-none">
      →
    </span>
  );
}
